import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { useI18n } from "../i18n/I18nProvider";
import { motion } from "framer-motion";
import {
  Link2, Plus, Copy, Check, Trash2, Eye, ExternalLink, Shield
} from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";

const ClientPortal = () => {
  const { token } = useAuth();
  const { t } = useI18n();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState("dashboard,compliance,risks");
  const [label, setLabel] = useState("");
  const [expiry, setExpiry] = useState("30");
  const [copied, setCopied] = useState("");

  const fetchLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/portal/links`, { headers: { Authorization: `Bearer ${token}` } });
      setLinks(res.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchLinks(); }, [fetchLinks]);

  const createLink = async () => {
    try {
      const res = await axios.post(`${API}/portal/create-link?modules=${modules}&expires_days=${expiry}${label ? `&label=${encodeURIComponent(label)}` : ''}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Portal link created");
      setLabel("");
      fetchLinks();
    } catch (error) { toast.error("Failed"); }
  };

  const deleteLink = async (portalId) => {
    try {
      await axios.delete(`${API}/portal/links/${portalId}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Deleted");
      fetchLinks();
    } catch (error) { toast.error("Failed"); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/portal/${token}`;
    navigator.clipboard.writeText(`${API.replace('/api','')}/api/portal/view/${token}`);
    setCopied(token);
    toast.success("Link copied");
    setTimeout(() => setCopied(""), 2000);
  };

  if (loading) return <div className="flex items-center justify-center h-[calc(100vh-4rem)]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="p-6 space-y-6" data-testid="client-portal-page">
      <PageHeader
        title={t("portal.title")}
        description={t("portal.subtitle")}
      />

      {/* Create Link */}
      <Card className="border-border" data-testid="create-link-card">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2"><Link2 className="w-5 h-5" /> Create Portal Link</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label>Label</Label>
              <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g., Client X Portal" data-testid="portal-label-input" />
            </div>
            <div className="space-y-2">
              <Label>Modules</Label>
              <Select value={modules} onValueChange={setModules}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dashboard,compliance,risks">Dashboard + Compliance + Risks</SelectItem>
                  <SelectItem value="dashboard,compliance">Dashboard + Compliance</SelectItem>
                  <SelectItem value="dashboard">Dashboard Only</SelectItem>
                  <SelectItem value="dashboard,compliance,risks,incidents">Full View</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Expires In</Label>
              <Select value={expiry} onValueChange={setExpiry}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="7">7 days</SelectItem>
                  <SelectItem value="30">30 days</SelectItem>
                  <SelectItem value="90">90 days</SelectItem>
                  <SelectItem value="365">1 year</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button onClick={createLink} className="w-full" data-testid="create-portal-link-btn">
                <Plus className="w-4 h-4 mr-2" /> Create Link
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Links List */}
      <div className="space-y-3">
        {links.map(link => (
          <Card key={link.portal_id} className="border-border" data-testid={`portal-link-${link.portal_id}`}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <Badge variant={link.active ? "default" : "secondary"}>{link.active ? "Active" : "Disabled"}</Badge>
                    {link.modules?.map(m => <Badge key={m} variant="outline" className="text-xs">{m}</Badge>)}
                  </div>
                  <p className="font-medium text-sm">{link.label}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                    <span>Views: {link.access_count || 0}</span>
                    <span>Expires: {new Date(link.expires_at).toLocaleDateString()}</span>
                    <span className="font-mono truncate max-w-48">{link.token}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => copyLink(link.token)}>
                    {copied === link.token ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </Button>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => deleteLink(link.portal_id)}>
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {links.length === 0 && (
          <div className="text-center py-12 text-muted-foreground">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No portal links created yet</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientPortal;
